import type { ReactNode } from 'react';
import styled from 'styled-components';
import Head from 'next/head';

import { media } from '../../../../helpers/breakpoints';

export interface HeroImageProps {
  section: string;
  cdnUrl: string;
  children?: ReactNode;
}

type ContainerProps = { section: string; cdnUrl: string };
const Container = styled.div<ContainerProps>`
  position: absolute;
  top: 0;
  width: 100%;
  height: var(--hero-image-height);

  background: ${({ cdnUrl, section }) =>
    `url('${cdnUrl}/images/hero/home/blurred/${section}-small-blur.jpeg')`};
  background-size: cover;
  background-position: center top;

  ${media.medium} {
    background-image: ${({ cdnUrl, section }) =>
      `url('${cdnUrl}/images/hero/home/blurred/${section}-medium-blur.jpeg')`};
  }

  ${media.large} {
    background-position: left top;
    background-image: ${({ cdnUrl, section }) =>
      `url('${cdnUrl}/images/hero/home/blurred/${section}-large-blur.jpeg')`};
  }
`;

type HighResProps = { section: string; cdnUrl: string };
const HighRes = styled.div<HighResProps>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  // This property is a fallback for browsers that don't support image-set
  background-image: ${({ cdnUrl, section }) =>
    `url('${cdnUrl}/images/hero/home/regular/${section}-small.jpeg')`};
  background-image: ${({ cdnUrl, section }) =>
    `image-set(
      '${cdnUrl}/images/hero/home/regular/${section}-small.avif' type("image/avif"),
      '${cdnUrl}/images/hero/home/regular/${section}-small.jpeg' type("image/jpeg")
    )`};
  background-position: center top;
  background-size: cover;

  ${media.medium} {
    // This property is a fallback for browsers that don't support image-set
    background-image: ${({ cdnUrl, section }) =>
      `url('${cdnUrl}/images/hero/home/regular/${section}-medium.jpeg')`};
    background-image: ${({ cdnUrl, section }) =>
      `image-set(
        '${cdnUrl}/images/hero/home/regular/${section}-medium.avif' type("image/avif"),
        '${cdnUrl}/images/hero/home/regular/${section}-medium.jpeg' type("image/jpeg")
      )`};
  }

  ${media.large} {
    background-position: left top;
    // This property is a fallback for browsers that don't support image-set
    background-image: ${({ cdnUrl, section }) =>
      `url('${cdnUrl}/images/hero/home/regular/${section}-large.jpeg')`};
    background-image: ${({ cdnUrl, section }) =>
      `image-set(
        '${cdnUrl}/images/hero/home/regular/${section}-large.avif' type("image/avif"),
        '${cdnUrl}/images/hero/home/regular/${section}-large.jpeg' type("image/jpeg")
      )`};
  }
`;

type OverlayProps = {
  section: string;
};
const Overlay = styled.div<OverlayProps>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    180deg,
    rgba(34, 40, 49, 0) 15.82%,
    rgba(34, 40, 49, 0.4) 19.04%,
    rgba(34, 40, 49, 0.6) 24.3%,
    rgba(34, 40, 49, 0.8) 30.43%
  );

  ${media.large} {
    background: linear-gradient(
      180deg,
      rgba(34, 40, 49, 0) 35.14%,
      rgba(34, 40, 49, 0.4) 54.91%,
      rgba(34, 40, 49, 0.6) 63.73%,
      rgba(34, 40, 49, 0.8) 78.03%
    );
  }
`;

const heroImagesToPreload: [string, string][] = [
  ['/images/hero/home/blurred/motor-small-blur.jpeg', '(max-width: 39.9375em)'],
  [
    '/images/hero/home/blurred/motor-medium-blur.jpeg',
    '(min-width: 40em) and (max-width: 63.9375em)',
  ],
  ['/images/hero/home/blurred/motor-large-blur.jpeg', '(min-width: 64em)'],
  [
    '/images/hero/home/blurred/marketplace-small-blur.jpeg',
    '(max-width: 39.9375em)',
  ],
  [
    '/images/hero/home/blurred/marketplace-medium-blur.jpeg',
    '(min-width: 40em) and (max-width: 63.9375em)',
  ],
  [
    '/images/hero/home/blurred/marketplace-large-blur.jpeg',
    '(min-width: 64em)',
  ],
  [
    '/images/hero/home/blurred/farming-small-blur.jpeg',
    '(max-width: 39.9375em)',
  ],
  [
    '/images/hero/home/blurred/farming-medium-blur.jpeg',
    '(min-width: 40em) and (max-width: 63.9375em)',
  ],
  ['/images/hero/home/blurred/farming-large-blur.jpeg', '(min-width: 64em)'],
];

function HeroImage({ children, cdnUrl, section }: HeroImageProps) {
  return (
    <>
      <Head>
        {heroImagesToPreload.map(([href, media], index) => (
          <link
            key={index}
            rel="preload"
            href={`${cdnUrl}${href}`}
            as="image"
            media={media}
          />
        ))}
      </Head>
      <Container section={section.toLowerCase()} cdnUrl={cdnUrl}>
        <HighRes section={section.toLowerCase()} cdnUrl={cdnUrl} />
        <Overlay section={section}>{children}</Overlay>
      </Container>
    </>
  );
}

export { HeroImage };
